import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormControlsOf } from '@y42/angular/forms';
import { getTrackByFn } from '@y42/angular/util';
import { CsvOptions, Y42FeatureType } from '@y42/json-schema';
import { tap } from 'rxjs';
import { Y42WriteSettings } from '../../state';
import {
  DatasourceTableSettingsCsvOptionsPropertyName,
  DatasourceTableSettingsDialogData,
  DatasourceTableSettingsDialogResult,
  DatasourceTableSettingsNavigationsConstant,
  DatasourceTableSettingsWriteSettingsPropertyName,
} from './model';

interface SettingsFormGroup {
  csv_options?: FormGroup<FormControlsOf<CsvOptions>>;
  _datos_write_settings?: FormGroup<FormControlsOf<Y42WriteSettings>>;
}

@UntilDestroy()
@Component({
  selector: 'y42-datasource-table-settings-dialog',
  templateUrl: './datasource-table-settings-dialog.component.html',
  styleUrls: ['./datasource-table-settings-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasourceTableSettingsDialogComponent {
  constructor(
    private readonly dialogRef: MatDialogRef<
      DatasourceTableSettingsDialogComponent,
      DatasourceTableSettingsDialogResult
    >,
    readonly breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA)
    readonly data: DatasourceTableSettingsDialogData,
  ) {
    this.breakpointObserver
      .observe(['(min-width: 1px) and (max-width: 767px)'])
      .pipe(
        tap((result) => {
          this.isSmallScreen = result.matches;
          this.isOpened = !result.matches;
        }),
        untilDestroyed(this),
      )
      .subscribe();

    this.data.tableSettings.forEach(([, settings]) => {
      const config: SettingsFormGroup = {};

      if (settings.csv_options) {
        config.csv_options = new FormGroup<FormControlsOf<CsvOptions>>({
          allow_quoted_new_lines: new FormControl(settings.csv_options.allow_quoted_new_lines, {
            validators: [Validators.required],
            nonNullable: true,
          }),
          encoding: new FormControl(settings.csv_options.encoding, {
            validators: [Validators.required],
            nonNullable: true,
          }),
          field_delimiter: new FormControl(settings.csv_options.field_delimiter, {
            validators: [Validators.required],
            nonNullable: true,
          }),
          ignore_unknown_values: new FormControl(settings.csv_options.ignore_unknown_values, {
            validators: [Validators.required],
            nonNullable: true,
          }),
          null_marker: new FormControl(settings.csv_options.null_marker, { nonNullable: true }),
          quote: new FormControl(settings.csv_options.quote, { validators: [Validators.required], nonNullable: true }),
          skip_leading_rows: new FormControl(settings.csv_options.skip_leading_rows, { nonNullable: true }),
        });
      }

      if (settings._datos_write_settings) {
        config._datos_write_settings = new FormGroup<FormControlsOf<Y42WriteSettings>>({
          max_flatten_depth: new FormControl(settings._datos_write_settings.max_flatten_depth, {
            validators: [Validators.required, Validators.min(0)],
            nonNullable: true,
          }),
        });
      }

      this.formTables.push(new FormGroup<SettingsFormGroup>(config));
    });
    this.activeTableSettingsId = this.data.tableSettings[0]?.[0];
  }

  form = new FormGroup<{
    tables: FormArray<FormGroup<SettingsFormGroup>>;
  }>({
    tables: new FormArray<FormGroup<SettingsFormGroup>>([]),
  });
  formTables = this.form.controls.tables;

  activeTableSettingsId: string | undefined;
  isSmallScreen = false;
  isOpened = true;
  tableSettingsNavigationsConstant = DatasourceTableSettingsNavigationsConstant;
  TableSettingsCsvOptionsPropertyName = DatasourceTableSettingsCsvOptionsPropertyName;
  TableSettingsWriteSettingsPropertyName = DatasourceTableSettingsWriteSettingsPropertyName;
  readonly Y42FeatureType = Y42FeatureType;
  readonly trackByKey = getTrackByFn<DatasourceTableSettingsDialogData['tableSettings'][number]>('0');

  toggleSideBar(state: boolean) {
    if (this.isSmallScreen) {
      this.isOpened = state;
    }
  }

  onSectionChange(sectionId: string) {
    this.activeTableSettingsId = sectionId;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  submit() {
    const settings = this.form.getRawValue().tables;
    const newSettings = this.data.tableSettings.map(([key], index) => [key, settings[index]!] as const);
    this.dialogRef.close({ tableSettings: newSettings });
  }

  scrollTo(sectionId: string, extraString?: string) {
    const elem = document.getElementById(sectionId + (extraString ? extraString : ''));
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.activeTableSettingsId = sectionId;
  }
}
